import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { TrendingUp, Loader2, MailCheck } from "lucide-react";
import AppFooter from "@/components/layout/AppFooter";

const RegisterPage = () => {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ full_name: "", email: "", password: "", confirm: "" });
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [done, setDone] = useState(false);

  const onChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setErr(null);
    if (form.password !== form.confirm) {
      setErr("Passwords do not match");
      return;
    }
    if (!acceptTerms) {
      setErr("Please accept the Terms & Conditions and Privacy Policy to continue.");
      return;
    }
    setBusy(true);
    try {
      await register({
        full_name: form.full_name,
        email: form.email,
        password: form.password,
      });
      setDone(true);
    } catch (e) {
      setErr(e.message || "Registration failed");
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div className="min-h-screen flex flex-col bg-background" data-testid="register-success">
        <div className="flex-1 grid place-items-center px-4 py-10">
        <Card className="w-full max-w-md border bg-card">
          <CardContent className="pt-6 text-center space-y-4">
            <div className="mx-auto inline-flex h-12 w-12 items-center justify-center rounded-xl bg-[hsl(var(--primary))]/10 text-[hsl(var(--primary))]">
              <MailCheck className="h-7 w-7" />
            </div>
            <h2 className="font-display text-lg font-semibold">Welcome to CSE Investor Beta!</h2>
            <p className="text-sm text-muted-foreground">
              Please check your email and click the verification link.
              Once verified, your account will be reviewed by our admin team
              and you'll get an email confirming approval.
            </p>
            <p className="text-sm text-foreground/90">
              You'll have full access to all features. Enjoy!
            </p>
            <div className="text-xs text-muted-foreground bg-muted rounded-md px-3 py-2">
              Sent to <span className="font-medium text-foreground">{form.email}</span>
            </div>
            <Button onClick={() => navigate("/login")} className="w-full mt-2" data-testid="register-success-back">
              Back to Sign in
            </Button>
          </CardContent>
        </Card>
        </div>
        <AppFooter />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-background" data-testid="register-page">
      <div className="flex-1 grid place-items-center px-4 py-10">
      <Card className="w-full max-w-md border bg-card">
        <CardHeader className="text-center space-y-1">
          <div className="mx-auto mb-2 inline-flex h-12 w-12 items-center justify-center rounded-xl bg-[hsl(var(--primary))]/10 text-[hsl(var(--primary))]">
            <TrendingUp className="h-6 w-6" />
          </div>
          <CardTitle className="font-display text-xl">Create your account</CardTitle>
          <CardDescription>Access dual-class CSE intelligence</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={submit} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="name">Full name</Label>
              <Input id="name" value={form.full_name} onChange={onChange("full_name")} required minLength={2} data-testid="reg-name" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" value={form.email} onChange={onChange("email")} required data-testid="reg-email" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password">Password</Label>
              <Input id="password" type="password" value={form.password} onChange={onChange("password")} required minLength={8} data-testid="reg-password" />
              <p className="text-[11px] text-muted-foreground">Minimum 8 characters.</p>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="confirm">Confirm password</Label>
              <Input id="confirm" type="password" value={form.confirm} onChange={onChange("confirm")} required minLength={8} data-testid="reg-confirm" />
            </div>
            <label
              htmlFor="reg-accept-terms"
              className={`flex gap-2.5 rounded-md border p-3 text-xs cursor-pointer transition-colors ${
                acceptTerms ? "bg-emerald-500/5 border-emerald-500/40" : "hover:bg-muted/50"
              }`}
              data-testid="reg-accept-terms-row"
            >
              <Checkbox
                id="reg-accept-terms"
                checked={acceptTerms}
                onCheckedChange={(v) => setAcceptTerms(!!v)}
                className="mt-0.5 shrink-0"
                data-testid="reg-accept-terms"
              />
              <span className="text-foreground leading-relaxed">
                I have read and agree to the{" "}
                <Link to="/terms" target="_blank" rel="noreferrer" className="underline hover:text-[hsl(var(--primary))]" data-testid="reg-link-terms">
                  Terms &amp; Conditions
                </Link>{" "}
                and the{" "}
                <Link to="/privacy" target="_blank" rel="noreferrer" className="underline hover:text-[hsl(var(--primary))]" data-testid="reg-link-privacy">
                  Privacy Policy
                </Link>
                . I understand CSE Investor is a research tool, not financial
                advice, and data may not be current or accurate.
              </span>
            </label>
            {err && (
              <div role="alert" className="rounded-md border border-[hsl(var(--destructive))]/30 bg-[hsl(var(--destructive))]/5 px-3 py-2 text-xs text-[hsl(var(--destructive))]" data-testid="reg-error">
                {err}
              </div>
            )}
            <Button type="submit" disabled={busy || !acceptTerms} className="w-full gap-2" data-testid="reg-submit">
              {busy && <Loader2 className="h-4 w-4 animate-spin" />}
              {busy ? "Creating…" : "Create account"}
            </Button>
          </form>
          <p className="mt-4 text-center text-xs text-muted-foreground">
            Already have an account?{" "}
            <Link to="/login" className="text-[hsl(var(--primary))] hover:underline" data-testid="link-login">Sign in</Link>
          </p>
        </CardContent>
      </Card>
      </div>
      <AppFooter />
    </div>
  );
};

export default RegisterPage;
